import { userScopeKey } from '../scope';
import { historyLogger, type HistoryLogger } from './logger';

/** The fields of a download summary this bridge reads (tolerant of other fields). */
export interface DownloadLike {
  itemId?: unknown;
  status?: unknown;
  createdAt?: unknown;
  serverUrl?: unknown;
  userId?: unknown;
  retryable?: unknown;
}

/** A download that appears within this window of its creation was started now (not restored at boot). */
const FRESH_DOWNLOAD_MS = 15_000;

const isActive = (d: DownloadLike) => d.status === 'queued' || d.status === 'downloading';

/**
 * A stopped download the user started again (resume, or retry after a hard
 * failure). Transient errors (`retryable`) are re-queued by the manager on its
 * own, which continues the same download rather than starting a new one.
 */
const isRestartable = (d: DownloadLike) => d.status === 'paused' || (d.status === 'error' && d.retryable !== true);

function scopeOf(d: DownloadLike): string | null {
  if (typeof d.serverUrl === 'string' && typeof d.userId === 'string') return userScopeKey({ serverUrl: d.serverUrl, userId: d.userId });
  return userScopeKey();
}

/**
 * Logs download history (DownloadProvider's producers) by watching the
 * downloads store: a new download or a restarted one → downloadStarted,
 * reaching `complete` → downloadCompleted, removal while queued or
 * downloading → downloadCancelled, removal of a finished download →
 * downloadRemoved. Removing a paused or failed download logs nothing, like
 * Flutter's `cancelDownload` for an inactive download.
 */
export function connectDownloadHistory(
  store: { get(): Readonly<Record<string, DownloadLike>>; subscribe(listener: () => void): () => void },
  opts: { logger?: HistoryLogger; now?: () => number } = {},
): () => void {
  const logger = opts.logger ?? historyLogger;
  const now = opts.now ?? Date.now;
  let previous = store.get();

  return store.subscribe(() => {
    const next = store.get();
    if (next === previous) return;
    for (const [id, d] of Object.entries(next)) {
      const before = previous[id];
      if (!before) {
        const createdAt = typeof d.createdAt === 'number' ? d.createdAt : 0;
        if (d.status !== 'complete' && now() - createdAt <= FRESH_DOWNLOAD_MS) void logger.logDownloadStarted(id, scopeOf(d));
      } else if (d.status === 'complete' && before.status !== 'complete') {
        void logger.logDownloadCompleted(id, scopeOf(d));
      } else if (isActive(d) && isRestartable(before)) {
        void logger.logDownloadStarted(id, scopeOf(d));
      }
    }
    for (const [id, d] of Object.entries(previous)) {
      if (next[id]) continue;
      if (d.status === 'complete') void logger.logDownloadRemoved(id, scopeOf(d));
      else if (isActive(d)) void logger.logDownloadCancelled(id, scopeOf(d));
    }
    previous = next;
  });
}
